import { useEffect, useMemo, useState } from "react"

import { FilterX } from "lucide-react"
import { toast } from "sonner"

import { ContentDispositionButton } from "@/components/buttons/ContentDispositionButton"
import { UploadDocumentsButton } from "@/components/buttons/UploadDocumentsButton"
import { DocumentDataTable } from "@/components/datatables/DocumentDataTable"
import { FiltersPanel } from "@/components/filters/FiltersPanel"
import { DocumentGrid } from "@/components/grids/DocumentGrid"
import { Button } from "@ui/button"
import { CONFIGURATION } from "@env/variables"
import { LAST_DISPLAYED_VIEW } from "@/constants/cookies"
import { DOCUMENT_FACETS } from "@/constants/facets"
import { transformPropertiesFacetsWithFilter, transformFacetStatusResponses, transformFacetTypeResponses } from "@/lib/facets"
import { removeEmptyValues } from "@/lib/filters"
import { FacetResponse, PropertyFacet, SearchFilters } from "@/models/facets"
import { useWatchDocumentSearchFacets, useWatchDocumentSearchResult } from "@/plugins/providers/document/DocumentSearchContext"
import { useUserSession } from "@session/UserSession"

import { VectorSearchQuery } from "@vertesia/common"

export function DocumentSearchPanel() {
    const { hasMore, search, isSearching, error, objects } = useWatchDocumentSearchResult()
    const facets = useWatchDocumentSearchFacets()

    const { project, typeRegistry } = useUserSession()
    const documentTypes = useMemo(() => typeRegistry?.types || [], [typeRegistry?.types])
    const filteredDocumentTypes = useMemo(() => documentTypes.filter((type) => !CONFIGURATION.TYPES_TO_EXCLUDE.includes(type.id)), [documentTypes])

    const [isGridView, setIsGridView] = useState(localStorage.getItem(LAST_DISPLAYED_VIEW) === "grid")

    const [filters, setFilters] = useState<SearchFilters>({})
    const [statuses, setStatuses] = useState<FacetResponse[]>([])
    const [properties, setProperties] = useState<PropertyFacet[]>([])
    const [types, setTypes] = useState<FacetResponse[]>([])
    const [users, setUsers] = useState<FacetResponse[]>([])

    const [loadingMore, setLoadingMore] = useState(false)
    const [isReady, setIsReady] = useState(true)

    const [clearFilters, setClearFilters] = useState(0)

    const searchObjects = async () => {
        const full_text = filters?.properties?.full_text
        const vectorText = filters?.vectorText as string
        const vector: VectorSearchQuery | undefined = vectorText ? {text: vectorText, config: {text: true, image: true, properties: true}} : undefined
        
        const properties = removeEmptyValues(filters?.properties)

        const query: any = filters
            ? {
                createdFrom: filters?.date?.createdFrom,
                createdTo: filters?.date?.createdTo,
                updatedFrom: filters?.date?.updatedFrom,
                updatedTo: filters?.date?.updatedTo,
                full_text,
                name: filters?.property?.name,
                status: filters?.status,
                types: filters?.type ? [filters.type] : filteredDocumentTypes.map((type) => type.id),
                vector,
                match: {
                    created_by: filters?.user,
                    ...properties
                }
            } : {}

        search.query = query

        search.search()
    }

    const loadMoreDocuments = () => {
        setLoadingMore(true)
        setIsReady(false)

        search
            .loadMore()
            .then(() => {
                setLoadingMore(false)
                setIsReady(true)
            })
            .catch(() => {
                setLoadingMore(false)
            })
    }

    const handleOnClearFilters = () => {
        setClearFilters(clearFilters + 1)
    }

    useEffect(() => {
        if (!error) {
            return
        }

        toast.error("Error while searching documents", {
            description: error.message,
            duration: 5000,
        })
    }, [error])

    useEffect(() => {
        setTypes(transformFacetTypeResponses(facets.type, typeRegistry))
        setStatuses(transformFacetStatusResponses(facets.status))
        setUsers(transformFacetStatusResponses((facets as any).created_by))
        setProperties(transformPropertiesFacetsWithFilter(facets, DOCUMENT_FACETS(project?.id || "")))
    }, [facets])

    useEffect(() => {
        searchObjects()
    }, [])

    return (
        <div className="flex h-full w-full bg-gray-100">
            <FiltersPanel properties={properties} statuses={statuses} types={types} users={users} onFiltersChange={setFilters} onSearch={() => searchObjects()} onClearFilters={clearFilters} />
            <div className="w-full h-full flex flex-col gap-4 p-4">
                <div className="w-full flex flex-row gap-2 items-center justify-end">
                    <div className="flex gap-4 items-center">
                        {filters && Object.keys(filters).length > 0 && (
                            <Button onClick={handleOnClearFilters}>
                                <FilterX />
                                <span>Clear Filter{Object.keys(filters).length > 1 ? "s" : ""}</span>
                                <span className="text-xs text-blue-500">({Object.keys(filters).length})</span>
                            </Button>
                        )}
                        <UploadDocumentsButton onUploadDone={() => searchObjects()} />
                        <ContentDispositionButton onUpdate={setIsGridView} />
                    </div>
                </div>
                <div className="flex-grow w-full overflow-auto">
                    {
                        isGridView
                            ? <DocumentGrid documents={objects} loading={isSearching} loadingMore={loadingMore} hasMore={hasMore} loadMore={loadMoreDocuments} displayScore={filters?.vectorText} />
                            : <DocumentDataTable data={objects} loading={isSearching} hasMore={hasMore && isReady} loadMore={loadMoreDocuments} displayScore={filters?.vectorText} />
                    }
                </div>
            </div>
        </div>
    )
}
